import { AxiosError } from 'axios'
import { isEmpty } from 'lodash'
import { action, computed, flow, makeAutoObservable, observable } from 'mobx'
import { computedFn } from 'mobx-utils'

import { deleteItemByIdFromArray, openPdfDocumentFromBlob, requestWrapper } from 'Shared/utils'

import { BillingLocation } from 'Core/common/BillingLocation/types'
import { STORE_UI } from 'Core/constants'
import { CustomGenerator, LoadingBtnNames } from 'Core/interfaces'
import {
	countryEntityToLabelValue,
	entityToLabelValue,
	formatStateProvince,
	getDefaultStoreArrayValue,
	getDefaultStoreValue
} from 'Core/utils'
import { StateProvince } from 'Core/utils/storeHelpers/types'

import {
	getRegistrationDocumentSaveMessage,
	getServiceLocationDeleteMessage,
	getServiceLocationSaveMessage,
	STORE_METADATA
} from 'Portals/Customer/constants'
import { CustomerRegistrationRequestStatus, RegistrationDocument } from 'Portals/Customer/entities'
import { RegistrationDocumentRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'
import { stateOptionToLabelValue } from 'Portals/Customer/utils'
import i18n from 'Portals/Customer/utils/i18nService'

export class RegistrationDocumentStore {
	@observable ownershipTypes = getDefaultStoreArrayValue()
	@observable states = getDefaultStoreArrayValue<StateProvince>()
	@observable bankruptcyTypes = getDefaultStoreArrayValue()
	@observable customerRegistrationData = getDefaultStoreValue<RegistrationDocument.Basic>()
	@observable documents = getDefaultStoreArrayValue<RegistrationDocument.Document.Basic>()
	@observable document = getDefaultStoreValue<RegistrationDocument.Document.Basic>()
	@observable file = getDefaultStoreValue<Blob>()
	@observable billingLocations = getDefaultStoreArrayValue<BillingLocation.Basic>()
	@observable billingLocation = getDefaultStoreValue<BillingLocation.Detailed>()
	@observable counties = getDefaultStoreArrayValue<Entity>()
	@observable deliveryTypes = getDefaultStoreArrayValue<Entity>()
	@observable purchaseOrderTypes = getDefaultStoreArrayValue<Entity>()
	@observable rebateMethods = getDefaultStoreArrayValue<Entity>()
	@observable serviceLocations =
		getDefaultStoreArrayValue<RegistrationDocument.ServiceLocation.Basic>()

	constructor(public rootStore: RootStore) {
		makeAutoObservable(this, {}, { autoBind: true })
	}

	@computed
	get areDocumentsEmpty() {
		return isEmpty(this.documents.value)
	}

	@computed
	get isPageLoading() {
		return (
			this.customerRegistrationData.isLoading ||
			this.ownershipTypes.isLoading ||
			this.bankruptcyTypes.isLoading
		)
	}

	@computed
	get ownershipTypesOptions() {
		return this.ownershipTypes.value.map(entityToLabelValue)
	}

	@computed
	get statesOptions() {
		return this.states.value.map(stateOptionToLabelValue)
	}

	@computed
	get isUpdatedByCustomerStatus() {
		return (
			this.customerRegistrationData.value?.status?.name ===
			CustomerRegistrationRequestStatus.updatedByCustomer
		)
	}

	@computed
	get isCompletedStatus() {
		return (
			this.customerRegistrationData.value?.status?.name ===
			CustomerRegistrationRequestStatus.completed
		)
	}

	@computed
	get isReadOnlyDocument() {
		return this.isUpdatedByCustomerStatus || this.isCompletedStatus
	}

	statesOptionsByCountryId = computedFn((countryId: BasicNumber) => {
		let filteredStates = this.states.value
		if (countryId) {
			filteredStates = filteredStates.filter((item) => item.countryId === countryId)
		}
		return filteredStates.map((item) => ({
			value: item.id,
			label: formatStateProvince(item)
		}))
	})

	stateById = computedFn((stateId: BasicNumber) => {
		if (stateId && this.states.value.length > 0) {
			return this.states.value.find(({ id }) => id === stateId)
		}
		return null
	})

	countryOptionsByCountryCode = computedFn((countryCode?: BasicString) => {
		let filteredCountries = this.rootStore[STORE_METADATA].countries.value
		if (countryCode) {
			filteredCountries = filteredCountries.filter(({ code }) => code === countryCode)
		}
		return filteredCountries.map(countryEntityToLabelValue)
	})

	@computed
	get bankruptcyTypesOptions() {
		return this.bankruptcyTypes.value.map(entityToLabelValue)
	}

	@flow
	*retrieveDocuments(
		customerRegistrationId: Key
	): CustomGenerator<RegistrationDocument.Document.Basic[]> {
		if (!customerRegistrationId) {
			return
		}

		yield requestWrapper({
			field: this.documents,
			repository: RegistrationDocumentRepo.retrieveDocuments,
			repositoryParams: customerRegistrationId
		})
	}

	@flow
	*retrieveDocument(documentId: Key): CustomGenerator<RegistrationDocument.Document.Basic> {
		const id = this.customerRegistrationData.value?.id

		if (!id) {
			return
		}

		yield requestWrapper({
			field: this.document,
			repository: RegistrationDocumentRepo.retrieveDocument,
			repositoryParams: { id, documentId }
		})
	}

	@flow
	*retrieveFile(documentId: Key): CustomGenerator<Blob> {
		const id = this.customerRegistrationData.value?.id

		if (!id) {
			return
		}

		yield requestWrapper({
			field: this.file,
			repository: RegistrationDocumentRepo.downloadDocument,
			repositoryParams: { id, documentId }
		})
	}

	@flow
	*uploadDocument(data: RegistrationDocument.Document.Request) {
		const id = this.customerRegistrationData.value?.id

		if (!id) {
			return
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield RegistrationDocumentRepo.uploadDocument(id, data)

			this.rootStore[STORE_UI].showSuccessNotification(getRegistrationDocumentSaveMessage())
			this.retrieveDocuments(id)
			this.rootStore[STORE_UI].closeModal()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsRequestSubmitting(false)
		}
	}

	@flow
	*downloadDocument(documentId: Key, documentName: string): CustomGenerator<Blob> {
		const id = this.customerRegistrationData.value?.id

		if (!id) {
			return
		}

		try {
			const document = yield RegistrationDocumentRepo.downloadDocument({ id, documentId })

			openPdfDocumentFromBlob(document, documentName)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow
	*downloadTemplateDocument(documentName: string): CustomGenerator<Blob> {
		try {
			const document = yield RegistrationDocumentRepo.downloadTemplateDocument()

			openPdfDocumentFromBlob(document, documentName)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow
	*retrieveOwnershipTypes(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.ownershipTypes,
			repository: RegistrationDocumentRepo.retrieveOwnershipTypes,
			repositoryParams: null
		})
	}

	@flow
	*retrieveStates(): CustomGenerator<StateProvince[]> {
		yield requestWrapper({
			field: this.states,
			repository: RegistrationDocumentRepo.retrieveStates,
			repositoryParams: null
		})
	}

	@flow
	*retrieveBankruptcyTypes(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.bankruptcyTypes,
			repository: RegistrationDocumentRepo.retrieveBankruptcyTypes,
			repositoryParams: null
		})
	}

	@flow
	*retrieveCustomerRegistrationData(): CustomGenerator<RegistrationDocument.Basic> {
		yield requestWrapper({
			field: this.customerRegistrationData,
			repository: RegistrationDocumentRepo.retrieveCustomerRegistrationData,
			repositoryParams: null
		})
	}

	@flow
	*retrieveRegistrationDocumentData() {
		yield this.retrieveCustomerRegistrationData()
		this.retrieveDocuments(this.customerRegistrationData.value!.id)
		this.retrieveBillingLocations(this.customerRegistrationData.value!.id)
		this.retrieveOwnershipTypes()
		this.retrieveStates()
		this.retrieveBankruptcyTypes()
		this.retrieveServiceLocations(this.customerRegistrationData.value!.id)
	}

	@flow
	*saveRegistrationForm(
		data: RegistrationDocument.Request,
		isSubmit?: boolean
	): CustomGenerator<void> {
		if (!this.customerRegistrationData.value) {
			return
		}
		this.rootStore[STORE_UI].setIsBtnLoading(
			isSubmit ? LoadingBtnNames.submit : LoadingBtnNames.save
		)
		try {
			const id = this.customerRegistrationData.value.id
			isSubmit
				? yield RegistrationDocumentRepo.submitRegistrationForm(data, id)
				: yield RegistrationDocumentRepo.saveRegistrationForm(data, id)
			this.retrieveCustomerRegistrationData()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsBtnLoading(null, false)
	}

	@action
	clearDocument() {
		this.document = getDefaultStoreValue<RegistrationDocument.Document.Basic>()
		this.file = getDefaultStoreValue<Blob>()
	}

	@flow
	*retrieveCounties(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.counties,
			repository: RegistrationDocumentRepo.retrieveCounties,
			repositoryParams: null
		})
	}

	@flow
	*retrieveDeliveryTypes(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.deliveryTypes,
			repository: RegistrationDocumentRepo.retrieveDeliveryTypes,
			repositoryParams: null
		})
	}

	@flow
	*retrievePurchaseOrderTypes(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.purchaseOrderTypes,
			repository: RegistrationDocumentRepo.retrievePurchaseOrderTypes,
			repositoryParams: null
		})
	}

	@flow
	*retrieveRebateMethods(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.rebateMethods,
			repository: RegistrationDocumentRepo.retrieveRebateMethods,
			repositoryParams: null
		})
	}

	@flow
	*retrieveBillingLocationData() {
		yield this.retrieveCounties()
		this.retrieveDeliveryTypes()
		this.retrievePurchaseOrderTypes()
		this.retrieveRebateMethods()
	}

	@computed
	get isBillingLocationFormLoading(): boolean {
		return (
			this.billingLocation.isLoading ||
			this.states.isLoading ||
			this.counties.isLoading ||
			this.purchaseOrderTypes.isLoading ||
			this.deliveryTypes.isLoading ||
			this.rebateMethods.isLoading
		)
	}

	@computed
	get countyOptions(): LabelValue<number>[] {
		return this.counties.value.map(entityToLabelValue)
	}

	@computed
	get purchaseOrderTypeOptions(): LabelValue<number>[] {
		return this.purchaseOrderTypes.value.map(entityToLabelValue)
	}

	@computed
	get deliveryTypeOptions(): LabelValue<number>[] {
		return this.deliveryTypes.value.map(entityToLabelValue)
	}

	@computed
	get rebateMethodOptions(): LabelValue<number>[] {
		return this.rebateMethods.value.map(entityToLabelValue)
	}

	@flow
	*retrieveBillingLocations(
		registrationDocumnetId: Key
	): CustomGenerator<BillingLocation.Basic[]> {
		yield requestWrapper({
			field: this.billingLocations,
			repository: RegistrationDocumentRepo.retreiveBillingLocations,
			repositoryParams: { registrationDocumnetId }
		})
	}

	@flow
	*retrieveBillingLocation(billingLocationId: Key): CustomGenerator<BillingLocation.Detailed> {
		if (!this.customerRegistrationData.value) {
			return
		}

		const registrationDocumnetId = this.customerRegistrationData.value.id

		yield requestWrapper({
			field: this.billingLocation,
			repository: RegistrationDocumentRepo.retreiveBillingLocation,
			repositoryParams: {
				billingLocationId,
				registrationDocumnetId
			}
		})
	}

	@action
	resetBillingLocation = () => {
		this.billingLocation = getDefaultStoreValue<BillingLocation.Detailed>()
	};

	@flow
	*saveBillingLocation(
		payload: BillingLocation.Request.Generic,
		billingLocationId?: Key
	): CustomGenerator<BillingLocation.Detailed> {
		if (!this.customerRegistrationData.value) {
			return
		}

		const registrationDocumnetId = this.customerRegistrationData.value.id

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			billingLocationId
				? yield RegistrationDocumentRepo.editBillingLocation(
						registrationDocumnetId,
						billingLocationId,
						payload
				  )
				: yield RegistrationDocumentRepo.addBillingLocation(registrationDocumnetId, payload)

			this.retrieveBillingLocations(registrationDocumnetId)
			this.rootStore[STORE_UI].closeModal()
			this.rootStore[STORE_UI].showSuccessNotification(
				i18n.t('partials:billingLocation.messages.BILLING_LOCATION_SAVE_MESSAGE')
			)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsRequestSubmitting(false)
		}
	}

	@flow
	*deleteBillingLocation(billingLocationId: Key) {
		if (!this.customerRegistrationData.value || !billingLocationId) {
			return
		}

		const registrationDocumnetId = this.customerRegistrationData.value.id

		try {
			this.billingLocations.isLoading = true
			yield RegistrationDocumentRepo.deleteBillingLocation(
				registrationDocumnetId,
				billingLocationId
			)

			this.billingLocations.value = deleteItemByIdFromArray(
				this.billingLocations.value,
				billingLocationId
			)
			this.billingLocations.isLoading = false

			this.rootStore[STORE_UI].showSuccessNotification(
				i18n.t('partials:billingLocation.messages.BILLING_LOCATION_DELETE_MESSAGE')
			)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow
	*retrieveServiceLocations(
		id: Key
	): CustomGenerator<RegistrationDocument.ServiceLocation.Basic[]> {
		yield requestWrapper({
			field: this.serviceLocations,
			repository: RegistrationDocumentRepo.retrieveServiceLocations,
			repositoryParams: id
		})
	}

	@flow
	*addServiceLocation(
		data: RegistrationDocument.ServiceLocation.Request.Generic
	): CustomGenerator<RegistrationDocument.ServiceLocation.Basic> {
		const { id } = this.customerRegistrationData.value || {}

		if (!id) {
			return
		}
		this.rootStore[STORE_UI].setIsBtnLoading(LoadingBtnNames.serviceLocationAdd)
		try {
			yield RegistrationDocumentRepo.addServiceLocation(id, data)
			this.retrieveServiceLocations(id)
			this.rootStore[STORE_UI].showSuccessNotification(getServiceLocationSaveMessage())
			this.rootStore[STORE_UI].closeModal()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsBtnLoading(null, false)
		}
	}

	@flow
	*updateServiceLocation(
		serviceLocationId: Key,
		data: RegistrationDocument.ServiceLocation.Request.Generic
	): CustomGenerator<RegistrationDocument.ServiceLocation.Basic> {
		const { id } = this.customerRegistrationData.value || {}

		if (!id || !serviceLocationId) {
			return
		}
		this.rootStore[STORE_UI].setIsBtnLoading(LoadingBtnNames.serviceLocationAdd)
		try {
			yield RegistrationDocumentRepo.updateServiceLocation(id, serviceLocationId, data)
			this.retrieveServiceLocations(id)
			this.rootStore[STORE_UI].showSuccessNotification(getServiceLocationSaveMessage())
			this.rootStore[STORE_UI].closeModal()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsBtnLoading(null, false)
		}
	}

	@flow
	*deleteServiceLocation(serviceLocationId: Key): CustomGenerator<void> {
		const { id } = this.customerRegistrationData.value || {}
		if (!id || !serviceLocationId) {
			return
		}

		try {
			yield RegistrationDocumentRepo.deleteServiceLocations(id, serviceLocationId)
			this.retrieveServiceLocations(id)
			this.rootStore[STORE_UI].showSuccessNotification(getServiceLocationDeleteMessage())
			this.rootStore[STORE_UI].closeModal()
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@action
	reset = (): void => {
		this.serviceLocations =
			getDefaultStoreArrayValue<RegistrationDocument.ServiceLocation.Basic>()
	}
}
